/*
 * File: SiteLineItemSelect.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 17 May 2021 2:13:24 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Form, Select, Spin, Tooltip } from 'antd';
import { useField } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setSiteId } from 'redux/productsInfo/productsInfoSlice';
import { ReduxState } from 'redux/rootReducer';
import { useGetSitesQuery } from 'redux/services/useKingstonLivingstonService';
import { useDebouncedCallback } from 'use-debounce';

const styles: InlineStylesModel = {
  selectInput: {
    width: '100%'
  },
  formItem: {
    marginBottom: 0
  }
};

interface SiteLineItemSelectProps {
  id: SalesOrderPayloadModel['lines'][number]['id'];
  siteId: SalesOrderPayloadModel['lines'][number]['siteId'];
  index: number;
}

export const SiteLineItemSelect = React.memo(({ index, id, siteId }: SiteLineItemSelectProps): JSX.Element => {
  const { pathname } = useLocation();
  const [x, _, { setValue }] = useField(`lines.${index}.siteId`);
  const form = Form.useFormInstance();
  const dispatch = useDispatch();

  const { selectedCompanyDataAreaId: dataAreaId } = useSelector((state: ReduxState) => state.salesOrders);
  const [inputValue, _setInputValue] = useState<string | undefined>(undefined);
  const [offset, _setOffset] = useState(0);
  const isPosted = pathname.includes('/posted');

  /* ****************** Hook API ****************** */
  const { data: siteResponse, isLoading, isFetching, isError } = useGetSitesQuery({ offset, dataAreaId, siteContains: inputValue });

  /* ****************** JSX / Variables ****************** */
  const optionsData = useMemo(() => siteResponse?.data.map((site) => ({ label: site.siteId, value: site.siteId })), [siteResponse]);
  const isDraftForm = pathname.includes('/drafts/');

  /* ****************** Functions ****************** */
  // handle delay of searching data
  const handleSearch = useDebouncedCallback((value: string) => {
    _setOffset(0);
    _setInputValue(value);
  }, 1000);

  const handleClear = (): void => {
    form.setFieldValue('warehouseId', '');
    form.setFieldValue('siteId', '');
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const target = event.currentTarget;

    if (!isLoading && !isFetching && target.scrollTop + target.offsetHeight === target.scrollHeight && siteResponse && siteResponse?.totalCount - offset > 25) {
      _setOffset((prevState) => prevState + 25);
    }
  };

  const handleSelect = (selectValue: string): void => {
    form.setFieldValue('warehouseId', '');
    dispatch(setSiteId(selectValue));
    form.setFieldValue('siteId', selectValue);

    setValue(selectValue);
    _setInputValue('');
  };

  const dropDownRender = (menu: JSX.Element): JSX.Element => {
    return <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>{isLoading || isFetching ? <Spin spinning /> : menu}</div>;
  };

  const errorMessage = useMemo((): string | undefined => {
    if (isError) return 'Unable to valid site on import';

    if (!siteId && id.includes('import')) return 'Required';
    if (!siteId && isDraftForm) return 'Required';

    return undefined;
  }, [isDraftForm, isError, id, siteId]);

  /* ****************** Render ****************** */
  return (
    <Tooltip title={errorMessage ? errorMessage : undefined}>
      <Select
        disabled={isPosted}
        allowClear
        dropdownRender={dropDownRender}
        filterOption={false}
        loading={isLoading || isFetching}
        onClear={handleClear}
        onPopupScroll={handleScroll}
        onSearch={handleSearch}
        onSelect={handleSelect}
        options={optionsData}
        showSearch
        value={form.getFieldValue('siteId')}
        style={styles.selectInput}
        status={errorMessage ? 'error' : undefined}
      />
    </Tooltip>
  );
});
